/**
 * BrandLogo Component
 * Displays brand logo if available, otherwise shows a stylized placeholder
 */

import { useState } from 'react';
import { Icon } from './icons';
import { clsx } from 'clsx';

interface BrandLogoProps {
  logoUrl?: string | null;
  brandName: string;
  primaryColor: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZES = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

const ICON_SIZES = {
  sm: 'sm' as const,
  md: 'md' as const,
  lg: 'lg' as const,
  xl: 'xl' as const,
};

const TEXT_SIZES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-2xl',
};

export function BrandLogo({
  logoUrl,
  brandName,
  primaryColor,
  size = 'md',
  className
}: BrandLogoProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Show logo if URL exists and hasn't errored
  if (logoUrl && !imageError) {
    return (
      <div
        className={clsx(
          SIZES[size],
          'rounded-lg overflow-hidden flex-shrink-0 bg-gray-100',
          className
        )}
      >
        <img
          src={logoUrl}
          alt={`${brandName} logo`}
          className={clsx(
            'w-full h-full object-contain',
            !imageLoaded && 'opacity-0'
          )}
          onError={() => setImageError(true)}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: primaryColor }}
          >
            <Icon name="loader" size={ICON_SIZES[size]} className="animate-spin text-white/70" />
          </div>
        )}
      </div>
    );
  }

  // Placeholder with brand icon and first letter
  return (
    <div
      className={clsx(
        SIZES[size],
        'rounded-lg flex items-center justify-center flex-shrink-0 relative overflow-hidden',
        className
      )}
      style={{ backgroundColor: primaryColor }}
    >
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 30% 30%, white 1px, transparent 1px)`,
          backgroundSize: '8px 8px',
        }}
      />

      {/* First letter or icon */}
      <span className={clsx(TEXT_SIZES[size], 'font-bold text-white relative z-10')}>
        {brandName?.[0]?.toUpperCase() || '?'}
      </span>
    </div>
  );
}
